import React from 'react';
import { Box, Button, ButtonProps, Chip, CircularProgress, Paper, PaperProps, TextField, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useUploadGuard } from '../hooks/useUploadGuard';
import { StageBackButton } from './StageWrapper';
import ResizableRequirementTextarea from './ResizableRequirementTextarea';

/** The optional-source cards used on both one-time requirement intake pages. */
export function OneTimeSupportDocumentCard({ dragging = false, hasDocuments = false, unsupported = false, busy = false, disabled = false, ...props }: PaperProps & { dragging?: boolean; hasDocuments?: boolean; unsupported?: boolean; busy?: boolean; disabled?: boolean }) {
  const { blockEvent, blockActivation } = useUploadGuard(disabled);
  return <Paper {...props} aria-disabled={disabled || undefined} onClickCapture={blockActivation} onKeyDownCapture={blockActivation} onChangeCapture={blockEvent} onDragEnterCapture={blockEvent} onDragOverCapture={blockEvent} onDropCapture={blockEvent} sx={{ p: 2, height: '100%', minHeight: 220, border: '2px dashed', borderColor: dragging ? 'primary.main' : unsupported ? 'warning.main' : hasDocuments ? 'success.main' : 'grey.300', backgroundColor: dragging ? 'action.hover' : unsupported ? 'warning.light' : hasDocuments ? 'success.light' : 'background.paper', display: 'flex', flexDirection: 'column', cursor: disabled ? 'not-allowed' : busy ? 'wait' : 'pointer', opacity: disabled ? 0.65 : 1, ...props.sx }}/>;
}

/** Shared presentation only: each workflow retains its own APIs and completion rules. */
export function OneTimeDocumentCard({ children, disabled = false, ...props }: PaperProps & { disabled?: boolean }) {
  const { blockEvent, blockActivation } = useUploadGuard(disabled);
  return (
    <Paper
      variant="outlined"
      {...props}
      aria-disabled={disabled || undefined}
      onClickCapture={blockActivation} onKeyDownCapture={blockActivation} onChangeCapture={blockEvent}
      onDragEnterCapture={blockEvent} onDragOverCapture={blockEvent} onDropCapture={blockEvent}
      sx={{
        p: 2, minHeight: 88, border: '2px dashed', borderColor: 'grey.300',
        backgroundColor: 'background.paper', display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', justifyContent: 'center', textAlign: 'left', cursor: 'pointer',
        ...props.sx,
        ...(disabled ? { cursor: 'not-allowed', opacity: 0.65 } : {}),
      }}
    >
      {children ?? <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}><Typography variant="caption" color="text.secondary">Drag &amp; drop or click to browse · .pdf, .docx, .txt, .md</Typography><CloudUploadIcon sx={{ fontSize: 24, color: 'text.secondary', flexShrink: 0 }}/></Box>
      </>}
    </Paper>
  );
}

interface RequirementInputProps {
  id: string;
  resizeKey: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  helper?: string;
  placeholder?: string;
  inputTestId?: string;
}

export function OneTimeRequirementInput({ id, resizeKey, label, value, onChange, disabled, helper, placeholder, inputTestId }: RequirementInputProps) {
  return (
    <Box sx={{ mb: 3 }} data-testid="one-time-requirement-input">
      <Typography component="label" htmlFor={id} variant="subtitle1" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
        {label}<Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
      </Typography>
      <ResizableRequirementTextarea
        id={id} resizeKey={resizeKey} value={value} onChange={event => onChange(event.target.value)}
        required disabled={disabled} inputTestId={inputTestId}
        textareaProps={{ 'aria-label': label }} placeholder={placeholder}
      />
      {helper && <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', display: 'block' }}>{helper}</Typography>}
    </Box>
  );
}

interface QuestionProps {
  id: string;
  number: number;
  question: string;
  required?: boolean;
  rationale?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  answerLabel?: string;
  testId?: string;
}

export function OneTimeQuestion({ id, number, question, required, rationale, value, onChange, disabled, answerLabel, testId }: QuestionProps) {
  return (
    <Box sx={{ mb: 1.5 }} data-testid={testId}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        <Chip size="small" label={required ? 'Required' : 'Optional'} color={required ? 'error' : 'success'}/>
        <Typography component="div" variant="body1" fontWeight={600}>{number}. {question}</Typography>
      </Box>
      {rationale && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{rationale}</Typography>}
      <TextField
        id={id} fullWidth multiline minRows={2} placeholder="Enter your answer..." required={required}
        inputProps={{ 'aria-label': answerLabel ?? question }} value={value} disabled={disabled}
        onChange={event => onChange(event.target.value)}
      />
    </Box>
  );
}

interface CharacteristicsEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  inputLabel?: string;
  testId?: string;
}

export function OneTimeCharacteristicsEditor({ label, value, onChange, readOnly, inputLabel, testId = 'characteristics-text' }: CharacteristicsEditorProps) {
  return (
    <Box data-testid="one-time-characteristics-editor">
      <Typography component="label" htmlFor="characteristics-editor" variant="subtitle1" gutterBottom sx={{ display: 'block', fontWeight: 600 }}>{label}</Typography>
      <TextField
        id="characteristics-editor" multiline minRows={10} fullWidth value={value}
        onChange={event => onChange(event.target.value)} variant="outlined" sx={{ mb: 2 }}
        data-testid={testId} inputProps={{ 'aria-label': inputLabel }} InputProps={{ readOnly }}
      />
    </Box>
  );
}

export function OneTimeDownloadButton({ loading, ...props }: ButtonProps & { loading?: boolean }) {
  return <Button variant="outlined" startIcon={loading ? <CircularProgress size={20} color="inherit"/> : <DownloadIcon/>} {...props}/>;
}

interface StageActionsProps {
  onBack?: () => void;
  onContinue?: () => void;
  nextLabel?: string;
  busy?: boolean;
  continueDisabled?: boolean;
}

export function OneTimeStageActions({ onBack, onContinue, nextLabel, busy = false, continueDisabled = false }: StageActionsProps) {
  return (
    <Box component="nav" aria-label="Stage actions" sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
      <Box>{onBack && <StageBackButton onClick={onBack} disabled={busy}/>}</Box>
      {onContinue && <Button variant="contained" disabled={busy || continueDisabled} onClick={onContinue}>{nextLabel || 'Continue'}</Button>}
    </Box>
  );
}
